import React, {useCallback, useMemo, useState} from 'react';
import AdminPage from '../common/AdminPage/AdminPage';
import {useIncidentList} from '../../../../store/incident';
import {incidentListGridId, useStoredGridState} from '../../../../store/grid';
import {IncidentListDTO} from 'hemwb-api';
import {Search} from '../../../core/Search/Search';
import AdminSpaceHeader from '../common/AdminSpaceHeader/AdminSpaceHeader';
import {Spin, Table, Button, Drawer, Dropdown} from 'antd';
import {MoreOutlined, EditOutlined, MailOutlined} from '@ant-design/icons';
import type {ColumnsType, TablePaginationConfig} from 'antd/es/table';
import type {MenuProps} from 'antd';
import type {SorterResult} from 'antd/es/table/interface';
import {fullName} from '../../../../store/user/utils';
import IncidentOverview from './common/incidentOverview/IncidentOverview';
import UpdateIncident from './UpdateIncident/UpdateIncident';
import {tid} from '../../../../testUtils';
import styles from './IncidentList.module.scss';

const TO_LOCALE_DATE_STRING_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

const truncate = (issueSummary: string, number: number) => {
  return issueSummary.length > number ? issueSummary.slice(0, number - 1) + ' ...' : issueSummary;
};

const searchIncidents = (incidents: IncidentListDTO[], search: string) => {
  if (!search) {
    return incidents;
  }
  const lcSearch = search.toLocaleLowerCase();
  return incidents.filter(
    (incident) =>
      incident.incidentId.toLocaleLowerCase().includes(lcSearch) ||
      incident.issueSummary.toLocaleLowerCase().includes(lcSearch) ||
      Object.values(incident.createdBy).some(
        (value) => !!value && typeof value === 'string' && value.toLocaleLowerCase().includes(lcSearch),
      ) ||
      (incident.modifiedBy !== null &&
        Object.values(incident.modifiedBy).some(
          (value) => !!value && typeof value === 'string' && value.toLocaleLowerCase().includes(lcSearch),
        )) ||
      incident.status.toLocaleLowerCase().includes(lcSearch),
  );
};

const IncidentList: React.FC = () => {
  const [pagination, setPagination] = useState<{current: number; pageSize: number}>({current: 1, pageSize: 10});
  const [sortField, setSortField] = useState<string | null>('modifiedOn');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<IncidentListDTO | null>(null);
  const gridState = useStoredGridState(incidentListGridId);
  const list = useIncidentList();

  const filteredIncident = useMemo(() => {
    return list && searchIncidents(list, gridState.search);
  }, [list, gridState.search]);

  const sortedIncidents = useMemo(() => {
    if (!sortField || !filteredIncident) return filteredIncident;
    return [...filteredIncident].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      if (sortField === 'incidentId') {
        aVal = a.incidentId;
        bVal = b.incidentId;
      } else if (sortField === 'issueSummary') {
        aVal = a.issueSummary;
        bVal = b.issueSummary;
      } else if (sortField === 'createdOn') {
        aVal = a.createdOn?.getTime() ?? 0;
        bVal = b.createdOn?.getTime() ?? 0;
      } else if (sortField === 'createdBy') {
        aVal = fullName(a.createdBy);
        bVal = fullName(b.createdBy);
      } else if (sortField === 'status') {
        aVal = a.status;
        bVal = b.status;
      } else if (sortField === 'modifiedOn') {
        aVal = a.modifiedOn?.getTime() ?? 0;
        bVal = b.modifiedOn?.getTime() ?? 0;
      } else if (sortField === 'modifiedBy') {
        aVal = fullName(a.modifiedBy);
        bVal = fullName(b.modifiedBy);
      }

      if (sortOrder === 'ascend') {
        return String(aVal).localeCompare(String(bVal));
      }
      return String(bVal).localeCompare(String(aVal));
    });
  }, [filteredIncident, sortField, sortOrder]);

  const handleSearchChange = useCallback(
    (search: string) => {
      setPagination({current: 1, pageSize: pagination.pageSize});
      gridState.setSearch(search);
    },
    [gridState, pagination.pageSize],
  );

  const handleTableChange = (
    pag: TablePaginationConfig,
    _: Record<string, unknown>,
    sorter: SorterResult<IncidentListDTO> | SorterResult<IncidentListDTO>[],
  ) => {
    if (pag.current) {
      setPagination({current: pag.current, pageSize: pag.pageSize || 10});
    }

    if (!Array.isArray(sorter) && sorter.field) {
      setSortField(sorter.field as string);
      setSortOrder((sorter.order as 'ascend' | 'descend') || 'ascend');
    } else {
      setSortField(null);
    }
  };

  const openEditDrawer = useCallback((incident: IncidentListDTO) => {
    setSelectedIncident(incident);
    setDrawerOpen(true);
  }, []);

  const closeEditDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedIncident(null);
  }, []);

  const handleEditSuccess = useCallback(() => {
    closeEditDrawer();
  }, [closeEditDrawer]);

  const getActionMenuItems = useCallback(
    (incident: IncidentListDTO): MenuProps['items'] => [
      {
        key: 'edit',
        icon: <EditOutlined style={{padding: '10px'}} />,
        label: 'Edit Status',
        onClick: () => openEditDrawer(incident),
      },
      {
        key: 'email',
        icon: <MailOutlined style={{padding: '10px'}} />,
        label: 'Email User',
        onClick: () => {
          window.location.href = `mailto:${incident.createdBy.email}`;
        },
      },
    ],
    [openEditDrawer],
  );

  const antdColumns: ColumnsType<IncidentListDTO> = useMemo(
    () => [
      {
        title: 'Incident by Id',
        dataIndex: 'incidentId',
        key: 'incidentId',
        sorter: true,
        width: '12%',
      },
      {
        title: 'Incident Summary',
        dataIndex: 'issueSummary',
        key: 'issueSummary',
        sorter: true,
        width: '20%',
        render: (text: string) => truncate(text, 20),
      },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        key: 'createdOn',
        sorter: true,
        width: '15%',
        render: (date: Date) =>
          date ? new Date(date).toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS) : null,
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        key: 'createdBy',
        sorter: true,
        width: '12%',
        render: (_text: any, record: IncidentListDTO) => fullName(record.createdBy),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        sorter: true,
        width: '12%',
      },
      {
        title: 'Modified On',
        dataIndex: 'modifiedOn',
        key: 'modifiedOn',
        sorter: true,
        width: '15%',
        defaultSortOrder: 'descend',
        render: (date: Date) =>
          date ? new Date(date).toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS) : null,
      },
      {
        title: 'Modified By',
        dataIndex: 'modifiedBy',
        key: 'modifiedBy',
        sorter: true,
        width: '12%',
        render: (_text: any, record: IncidentListDTO) => (record.modifiedBy ? fullName(record.modifiedBy) : null),
      },
      {
        title: '',
        key: 'actions',
        width: '4%',
        align: 'right' as const,
        render: (_text: any, record: IncidentListDTO) => (
          <Dropdown
            menu={{items: getActionMenuItems(record)}}
            trigger={['click']}
            overlayClassName={styles.actionsMenuDropdown}
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
              {...tid('btn', 'incident-row-actions', record.incidentId)}
            />
          </Dropdown>
        ),
      },
    ],
    [getActionMenuItems],
  );

  const isLoading = !list;

  return (
    <>
      <AdminPage style={{paddingLeft: '2rem'}} header={<AdminSpaceHeader label="Analyze the incidents" />}>
        <div className={styles.listPanel}>
          <Search value={gridState.search} onValueChange={handleSearchChange} />
        </div>
        <Spin spinning={isLoading}>
          <div className={styles.recordsContainer}>
            <Table<IncidentListDTO>
              className={styles.tableWrapper}
              columns={antdColumns}
              dataSource={sortedIncidents || []}
              rowKey={(record) => record.incidentId}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: sortedIncidents?.length || 0,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              onChange={handleTableChange}
              expandable={{
                expandedRowKeys: expandedRowId ? [expandedRowId] : [],
                onExpandedRowsChange: (keys) => {
                  setExpandedRowId(keys.length > 0 ? (keys[0] as string) : null);
                },
                expandIcon: ({expanded, onExpand, record}) => (
                  <Button
                    type="text"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      onExpand(record, event);
                    }}
                    style={{
                      fontSize: '1.8rem',
                      fontWeight: 700,
                      lineHeight: 1,
                      color: '#1f252c',
                      paddingInline: 0,
                    }}
                  >
                    {expanded ? 'v' : '>'}
                  </Button>
                ),
                expandedRowRender: (record) => <IncidentOverview incident={record} />,
              }}
              locale={{emptyText: 'No incidents found'}}
              {...tid('incident-list-table')}
            />
          </div>
        </Spin>
      </AdminPage>

      <Drawer
        open={drawerOpen}
        onClose={closeEditDrawer}
        placement="right"
        width={420}
        className="whiteTheme"
        title={null}
        closable={false}
        styles={{body: {padding: '2.4rem', paddingTop: '1.6rem'}}}
      >
        {selectedIncident && (
          <UpdateIncident
            status={selectedIncident.status}
            incidentId={selectedIncident.incidentId}
            issueSummary={selectedIncident.issueSummary}
            reportId={selectedIncident.reportId}
            onSuccess={handleEditSuccess}
            onCancel={closeEditDrawer}
          />
        )}
      </Drawer>
    </>
  );
};

export default IncidentList;
